import { useState, useEffect } from 'react';
import { X, Share, Plus, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';

export function IOSInstallBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Detect iOS device
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if already installed (running as standalone PWA)
    const standalone = window.navigator.standalone === true || 
                       window.matchMedia('(display-mode: standalone)').matches;
    setIsStandalone(standalone);

    // Check if user has dismissed the banner before
    const dismissed = localStorage.getItem('ios_install_banner_dismissed');
    const dismissedTime = dismissed ? parseInt(dismissed) : 0;
    const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);

    // Show banner if: iOS + not standalone + not dismissed (or dismissed > 7 days ago)
    if (iOS && !standalone && (!dismissed || daysSinceDismissed > 7)) {
      // Delay showing the banner for better UX
      setTimeout(() => setShowBanner(true), 3000);
    }
  }, []);

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('ios_install_banner_dismissed', Date.now().toString());
  };

  const handleNeverShow = () => {
    setShowBanner(false);
    // Set a far future date to effectively never show again
    localStorage.setItem('ios_install_banner_dismissed', (Date.now() + 365 * 24 * 60 * 60 * 1000 * 10).toString());
  };

  if (!showBanner || !isIOS || isStandalone) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-slide-up" data-testid="ios-install-banner">
      <div className="max-w-lg mx-auto bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl shadow-2xl border border-gray-700 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <Smartphone className="w-5 h-5" />
            <span className="font-semibold">Install WorkForce Pro</span>
          </div>
          <button 
            onClick={handleDismiss}
            className="text-white/80 hover:text-white transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 text-white">
          <p className="text-sm text-gray-300 mb-4">
            Install this app on your iPhone for the best experience with quick access from your home screen.
          </p>

          {/* Steps */}
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
                <span className="text-blue-400 font-bold text-sm">1</span>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">Tap the Share button</p>
                <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                  Look for the 
                  <span className="inline-flex items-center justify-center w-6 h-6 bg-gray-700 rounded">
                    <Share className="w-4 h-4 text-blue-400" style={{ transform: 'translateY(-1px)' }} />
                  </span>
                  icon at the bottom of Safari
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
                <span className="text-blue-400 font-bold text-sm">2</span>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">Scroll down and tap "Add to Home Screen"</p>
                <p className="text-xs text-gray-400 flex items-center gap-1 mt-1">
                  Look for the
                  <span className="inline-flex items-center justify-center w-6 h-6 bg-gray-700 rounded">
                    <Plus className="w-4 h-4 text-gray-300" />
                  </span>
                  Add to Home Screen option
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center">
                <span className="text-green-400 font-bold text-sm">3</span>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">Tap "Add" to install</p>
                <p className="text-xs text-gray-400 mt-1">
                  The app will appear on your home screen like a regular app!
                </p>
              </div>
            </div>
          </div>

          {/* Note about Safari */}
          <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
            <p className="text-xs text-yellow-200">
              <strong>Note:</strong> This only works in Safari. If you're using Chrome or another browser, please open this page in Safari first.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-2 mt-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleNeverShow}
              className="text-gray-400 hover:text-white hover:bg-gray-700 text-xs"
            >
              Don't show again
            </Button>
            <Button 
              size="sm" 
              onClick={handleDismiss}
              className="ml-auto bg-blue-600 hover:bg-blue-700 text-white"
            >
              Got it!
            </Button>
          </div>
        </div>
      </div>

      {/* Animation styles */}
      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.4s ease-out forwards;
        }
      `}</style>
    </div>
  );
}

export default IOSInstallBanner;
